import { useState, useRef } from "react";
import { Chat } from "../types";
import { createDefaultChat } from "../utils/chatStateTransformers";
import { fetchChatsFromSupabase } from "../utils/supabaseDataUtils";
import { useChatCache } from "./useChatCache";
import { useChatSubscriptions } from "./useChatSubscriptions";

export function useChatInitialization() {
  const [chats, setChats] = useState<Chat[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const isMounted = useRef(true);
  const isRefreshingRef = useRef(false);
  const lastSuccessfulFetchRef = useRef(0);
  const initializeAttemptedRef = useRef(false);

  const { getChatWithMessages } = useChatCache();
  const { setupChatMessageSubscription, setupRealtimeSubscription } =
    useChatSubscriptions();

  const initializeChats = async () => {
    if (chats.length > 0) return;
    try {
      const chatData = await fetchChatsFromSupabase();
      const chatsWithMessages = await Promise.all(
        chatData.map(async (chat) => {
          const transformedChat = await getChatWithMessages(chat);
          return transformedChat;
        })
      );
      setChats(chatsWithMessages.filter((chat) => chat !== null) as Chat[]);
    } catch (error) {
      console.log(error);
    }
  };

  const manualRefresh = async () => {
    console.log("Refreshing chats... [REFRESHING HATS]");

    try {
      const chatData = await fetchChatsFromSupabase();

      if (!chatData || chatData.length === 0) {
        console.log("No chats found during manual refresh");
        return true;
      }

      const chatsPromises = chatData.map(async (chat: any) => {
        try {
          const transformedChat = await getChatWithMessages(chat, true);

          if (transformedChat) {
            setupChatMessageSubscription(
              transformedChat.id,
              (newMessage) => {
                if (!isMounted.current) return;

                setChats((currentChats) => {
                  const chatIndex = currentChats.findIndex(
                    (c) => c.id === transformedChat.id
                  );
                  if (chatIndex === -1) return currentChats;

                  const updatedChats = [...currentChats];
                  const chat = { ...updatedChats[chatIndex] };

                  if (chat.messages.some((m) => m.id === newMessage.id)) {
                    return currentChats; // No change needed
                  }

                  chat.messages = [...chat.messages, newMessage];
                  chat.lastActivity = Date.now();
                  updatedChats[chatIndex] = chat;
                  return updatedChats;
                });
              },
              isMounted
            );
          }

          return transformedChat;
        } catch (error) {
          console.log(`Error fetching messages for chat ${chat.id}:`, error);
          return null;
        }
      });

      const chatsWithMessages = await Promise.all(chatsPromises);

      if (!isMounted.current) return false;

      const validChats = chatsWithMessages.filter(
        (chat) => chat !== null
      ) as Chat[];
      if (validChats.length > 0) {
        setChats(validChats);
      }

      return true;
    } catch (error) {
      console.error("Error in manual refresh:", error);
      return false;
    }
  };

  const refreshChats = async () => {
    console.log("Refreshing chats... [REFRESHING HATS]");
    if (!isRefreshingRef.current && isMounted.current) {
      setIsLoading(true);
      isRefreshingRef.current = true;

      try {
        const chatData = await fetchChatsFromSupabase();

        if (!isMounted.current) {
          console.log("Component unmounted during refresh, aborting");
          return false;
        }

        if (!chatData || chatData.length === 0) {
          console.log("No chats found during manual refresh");
          setChats([createDefaultChat()]);
          setIsLoading(false);
          lastSuccessfulFetchRef.current = Date.now();
          isRefreshingRef.current = false;
          return true;
        }

        const chatsPromises = chatData.map(async (chat: any) => {
          try {
            const transformedChat = await getChatWithMessages(chat, true);

            if (transformedChat) {
              setupChatMessageSubscription(
                transformedChat.id,
                (newMessage) => {
                  if (!isMounted.current) return;

                  setChats((currentChats) => {
                    const chatIndex = currentChats.findIndex(
                      (c) => c.id === transformedChat.id
                    );
                    if (chatIndex === -1) return currentChats;

                    const updatedChats = [...currentChats];
                    const chat = { ...updatedChats[chatIndex] };

                    if (chat.messages.some((m) => m.id === newMessage.id)) {
                      return currentChats; // No change needed
                    }

                    chat.messages = [...chat.messages, newMessage];
                    chat.lastActivity = Date.now();
                    updatedChats[chatIndex] = chat;
                    return updatedChats;
                  });
                },
                isMounted
              );
            }

            return transformedChat;
          } catch (error) {
            console.error(
              `Error fetching messages for chat ${chat.id}:`,
              error
            );
            return null;
          }
        });

        const chatsWithMessages = await Promise.all(chatsPromises);

        if (!isMounted.current) return false;

        const validChats = chatsWithMessages.filter(
          (chat) => chat !== null
        ) as Chat[];
        if (validChats.length > 0) {
          setChats(validChats);
        } else {
          setChats([createDefaultChat()]);
        }

        return true;
      } catch (error) {
        console.error("Error in manual refresh:", error);
        return false;
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
          isRefreshingRef.current = false;
        }
      }
    }
    return false;
  };

  return {
    chats,
    setChats,
    isLoading,
    initializeChats,
    refreshChats,
    manualRefresh,
    isMounted,
    isRefreshingRef,
    lastSuccessfulFetchRef,
    initializeAttemptedRef,
    setupRealtimeSubscription,
  };
}
